import React, { useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { request } from "../../../../utils/Request";
import { isEmpty, orderBy } from "lodash";
import { Col, Row } from "react-bootstrap";
import { TabPanel, TabView } from "primereact/tabview";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import AlpOutboundLoadStatus from "../../alpOutboundLoads/partials/AlpOutboundLoadStatus";
import Location from "../../../../components/Common/Display/Location";
import VinWithType from "../../../../components/Common/Display/VinWithType";
import { AlpOutboundLoadGroupStatusChips, GateOutStatusChip } from "../../loads/partials/LoadStatusChip";

const AlpAvisoOutboundGroupDetailPage = (props: any) => {
    const { t, toastify, groupToken } = props;
    const [entity, setEntity] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [highlightLoadKey, setHighlightLoadKey] = useState<string | null>(null);
    const [highlightHistoryKey, setHighlightHistoryKey] = useState<string | null>(null);

    const highlightTimeoutRef = useRef<any>(null);
    const loadRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const historyRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const historyContainerRef = useRef<HTMLDivElement | null>(null);

    const getCurrentCmrDocuments = (cmrDocuments: any): any[] => {
        if ( Array.isArray(cmrDocuments) ) {
            return cmrDocuments;
        }

        return Array.isArray(cmrDocuments?.current) ? cmrDocuments.current : [];
    };

    const getPreviousCmrDocuments = (cmrDocuments: any): any[] => {
        return Array.isArray(cmrDocuments?.previous) ? cmrDocuments.previous : [];
    };

    const getCmrDocumentRows = (cmrDocuments: any): any[] => {
        const current = orderBy(getCurrentCmrDocuments(cmrDocuments), ['created_at'], ['desc'])
            .map((cmr: any) => ({ cmr, label: 'current cmr' }));
        const previous = orderBy(getPreviousCmrDocuments(cmrDocuments), ['deleted_at', 'created_at'], ['desc', 'desc'])
            .map((cmr: any) => ({ cmr, label: 'previous cmr' }));

        return [...current, ...previous];
    };

    const highlightOnce = (type: 'load' | 'history', key: string) => {
        if ( highlightTimeoutRef.current ) {
            clearTimeout(highlightTimeoutRef.current);
        }

        if ( type === 'load' ) {
            setHighlightLoadKey(key);
        } else {
            setHighlightHistoryKey(key);
        }

        highlightTimeoutRef.current = setTimeout(() => {
            if ( type === 'load' ) {
                setHighlightLoadKey(null);
            } else {
                setHighlightHistoryKey(null);
            }
        }, 1400);
    };

    const getDetail = async () => {
        if ( isEmpty(groupToken) ) {
            return;
        }
        setLoading(true);
        try {
            const response = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const data = response?.data?.data ?? response?.data;
            setEntity(data);
        } catch (e: any) {
            if ( e && e.status === 422 ) {
                toastify && toastify(t("unprocessable entity"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setEntity(null);
        getDetail();
    }, [groupToken]);

    const loads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];
    const histories = Array.isArray(entity?.status_histories) ? entity.status_histories : [];

    const loadNumberToKey = useMemo(() => {
        const map: Record<string, string> = {};
        loads.forEach((load: any, idx: number) => {
            const key = `${ load?.id ?? load?.guid ?? load?.load_number ?? idx }`;
            const number = `${ load?.load_number ?? '' }`.trim();
            if ( !isEmpty(number) ) {
                // normalize to avoid mismatch (case/whitespace)
                map[number.toLowerCase()] = key;
            }
        });
        return map;
    }, [loads]);

    const groupLoadNumbers = useMemo(() => {
        if ( Array.isArray(entity?.load_numbers) ) {
            return entity.load_numbers
            .filter((n: any) => !isEmpty(n))
            .map((n: any) => `${ n }`.trim());
        }

        // fallback: build from loads
        return loads
        .map((l: any) => `${ l?.load_number ?? '' }`.trim())
        .filter((n: string) => !isEmpty(n));
    }, [entity, loads]);

    const scrollToLoadByNumber = (loadNumber: string) => {
        const normalized = `${ loadNumber }`.trim().toLowerCase();
        let key = loadNumberToKey[normalized];

        // Fallback: search in loads array in case mapping misses
        if ( isEmpty(key) ) {
            const found = loads.find((l: any, idx: number) => ( `${ l?.load_number ?? '' }`.trim().toLowerCase() === normalized ));
            if ( found ) {
                key = `${ found?.id ?? found?.guid ?? found?.load_number ?? 0 }`;
            }
        }

        if ( isEmpty(key) ) {
            return;
        }

        const el = loadRefs.current[key];
        if ( el && typeof el.scrollIntoView === 'function' ) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        highlightOnce('load', key);
    };

    const KeyValueRow = ({ label, value }: { label: any, value: any }) => {
        return (
            <Row className="align-items-start">
                <Col md={ 6 } sm={ 12 }>
                    <strong className="text-capitalize">{ label }</strong>
                </Col>
                <Col md={ 6 } sm={ 12 } className="text-md-end">
                    { typeof value === 'string' || typeof value === 'number'
                        ? <div>{ value }</div>
                        : <div>{ value }</div> }
                </Col>
            </Row>
        );
    };

    return (
        // Parent wrapper should NOT be a single big white block; use spaced cards instead.
        <div className={ "bg-body-tertiary pt-3" }>
            { loading && (
                <div className="border rounded-3 p-3 bg-white text-muted small">
                    { t("loading") }...
                </div>
            ) }

            { entity && (
                <Row className="g-3">
                    {/* Left: all loads (stacked cards) */ }
                    <Col md={ 8 } lg={ 8 }>
                        { loads.length <= 0 && (
                            <div className="border rounded-3 p-4 bg-white shadow-sm text-muted small">
                                { t("no records found") }
                            </div>
                        ) }

                        { loads.length > 0 && (
                            <div className="d-flex flex-column gap-3">
                                { loads.map((load: any, idx: number) => {
                                    const loadKey = `${ load?.id ?? load?.guid ?? load?.load_number ?? idx }`;
                                    const isHighlighted = highlightLoadKey === loadKey;
                                    return (
                                        <div
                                            key={ loadKey }
                                            ref={ (el) => {
                                                loadRefs.current[loadKey] = el;
                                            } }
                                            className={ `rounded-3 p-4 bg-white shadow-sm ${ isHighlighted ? 'border border-2 border-primary' : 'border' }` }
                                        >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="flex-grow-1">
                                                    <KeyValueRow
                                                        label={ t("load number") }
                                                        value={ load.load_number || `${ t("load") } #${ idx + 1 }` }
                                                    />
                                                </div>
                                                <div className="ms-3" style={ { minWidth: 220 } }>
                                                    <KeyValueRow
                                                        label={ t("status") }
                                                        value={ <AlpOutboundLoadStatus status={ load.status } chip={ true } /> }
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-2" />

                                            <Row className="g-2">
                                                <Col md={ 6 }>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("supplier compound") }
                                                                     value={ load.supplier_compound || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("from location") }
                                                                     value={ load.from_location.label || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("dealer") }
                                                                     value={ load.dealer_name || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("driver") }
                                                                     value={ load.driver_name || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("transport vehicle") }
                                                            value={
                                                                `${ load.truck_plate_number || "" }${ load.trailer_plate_number ? ` | ${ load.trailer_plate_number }` : "" }` || "-"
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("cmr") } value={ load.cmr || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("efl price") }
                                                                     value={ load.emil_frey_price || "-" } />
                                                    </div>
                                                </Col>

                                                <Col md={ 6 }>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("created at") }
                                                            value={ load.created_at ? utcDateToLocalDate(load.created_at) : "-" }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow label={ t("to location") }
                                                                     value={ load.to_location.label || "-" } />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("estimated loading time") }
                                                            value={ load.estimated_loading_time ? utcDateToLocalDate(load.estimated_loading_time) : "-" }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("estimated unloading") }
                                                            value={ load.estimated_unloading_time ? utcDateToLocalDate(load.estimated_unloading_time) : "-" }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("gate out") }
                                                            value={
                                                                <GateOutStatusChip
                                                                    confirmedAt={ load?.gate_out_confirmed_at }
                                                                    t={ t }
                                                                    dateLabel={
                                                                        !isEmpty(load?.gate_out_confirmed_at)
                                                                            ? utcDateToLocalDate(load.gate_out_confirmed_at)
                                                                            : undefined
                                                                    }
                                                                />
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <KeyValueRow
                                                            label={ t("location") }
                                                            value={
                                                                <div>
                                                                    <Location item={ load.from_location }
                                                                              titled={ 1 } />
                                                                    <div><Location item={ load.to_location }
                                                                                   titled={ 1 } /></div>
                                                                </div>
                                                            }
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="mt-3">
                                                <TabView>
                                                    <TabPanel header={ t("orders") }>
                                                        <div className="table-responsive mt-2">
                                                            <table className={ "fl-table w-full" }>
                                                                <thead>
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>{ t('vin number') }</th>
                                                                        <th>{ t('brand') }</th>
                                                                        <th>{ t('status') }</th>
                                                                        <th>{ t('efl price') }</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    { ( load.alp_outbound_load_orders || [] ).map((orderRow: any, orderIndex: number) => (
                                                                        <tr key={ orderIndex }>
                                                                            <td>{ orderIndex + 1 }</td>
                                                                            <td>
                                                                                <VinWithType
                                                                                    vinNumber={ orderRow?.order?.vin_number } />
                                                                                <div
                                                                                    className="text-muted small">{ orderRow?.order?.order_cd }</div>
                                                                            </td>
                                                                            <td>
                                                                                    <span className="text-capitalize">
                                                                                        { orderRow?.order?.brand?.title }
                                                                                        <span
                                                                                            className="text-capitalize d-block">
                                                                                            { orderRow?.order?.brand_model?.title } - { orderRow?.order?.brand_model?.version_code }
                                                                                        </span>
                                                                                    </span>
                                                                            </td>
                                                                            <td>
                                                                                <AlpOutboundLoadStatus status={ orderRow?.status } chip={ true } />
                                                                            </td>
                                                                            <td><span
                                                                                className={ "text-uppercase" }>{ `${ orderRow?.emil_frey_price ?? '' }` }</span>
                                                                            </td>
                                                                        </tr>
                                                                    )) }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </TabPanel>

                                                    <TabPanel header={ t("status histories") }>
                                                        <div className="table-responsive mt-2">
                                                            <table className={ "fl-table w-full" }>
                                                                <thead>
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>{ t('author') }</th>
                                                                        <th>{ t('status') }</th>
                                                                        <th>{ t('comments') }</th>
                                                                        <th>{ t('created at') }</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    { orderBy(load.status_histories || [], ['created_at'], ['desc']).map((h: any, i: number) => (
                                                                        <tr key={ i }>
                                                                            <td>{ i + 1 }</td>
                                                                            <td>{ h?.author?.name ?? '-' }</td>
                                                                            <td>
                                                                                <AlpOutboundLoadStatus status={ h?.status } chip={ true } />
                                                                            </td>
                                                                            <td>
                                                                                { h?.comments }
                                                                                <div
                                                                                    className="text-muted small">{ h?.system_note }</div>
                                                                            </td>
                                                                            <td>{ h?.created_at ? utcDateToLocalDate(h.created_at) : '-' }</td>
                                                                        </tr>
                                                                    )) }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </TabPanel>

                                                    <TabPanel header={ t("documents") }>
                                                        <div className="table-responsive mt-2">
                                                            <table className={ "fl-table w-full" }>
                                                                <thead>
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>{ t('title') }</th>
                                                                        <th>{ t('type') }</th>
                                                                        <th>{ t('created at') }</th>
                                                                        <th>{ t('action') }</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    { orderBy(load.alp_documents || [], ['created_at'], ['desc']).map((doc: any, i: number) => (
                                                                        <tr key={ i }>
                                                                            <td>{ i + 1 }</td>
                                                                            <td>{ doc?.title ?? '-' }</td>
                                                                            <td>{ doc?.additional_info_one ?? '-' }</td>
                                                                            <td>{ doc?.created_at ? utcDateToLocalDate(doc.created_at) : '-' }</td>
                                                                            <td>
                                                                                { doc?.alp_document_url && (
                                                                                    <a href={ doc.alp_document_url }
                                                                                       target="_blank" rel="noreferrer">
                                                                                        <i className="fas fa-download"></i>
                                                                                    </a>
                                                                                ) }
                                                                            </td>
                                                                        </tr>
                                                                    )) }
                                                                    { getCmrDocumentRows(load.cmr_documents).map((item: any, i: number) => {
                                                                        const cmr = item.cmr;

                                                                        return (
                                                                        <tr key={ `cmr-${ item.label }-${ cmr?.id ?? i }` }>
                                                                            <td>{ (load.alp_documents || []).length + i + 1 }</td>
                                                                            <td>
                                                                                <div>{ cmr?.cmr_number ?? '-' }</div>
                                                                                <div className="text-muted small">{ t(item.label) }</div>
                                                                            </td>
                                                                            <td>{ t('cmr') }</td>
                                                                            <td>{ cmr?.created_at ? utcDateToLocalDate(cmr.created_at) : '-' }</td>
                                                                            <td>
                                                                                { cmr?.document?.id && (
                                                                                    <a
                                                                                        href={ `${ process.env.REACT_APP_DOCUMENT_URL }/${ cmr.document.id }` }
                                                                                        target="_blank"
                                                                                        rel="noreferrer"
                                                                                    >
                                                                                        <i className="fas fa-download"></i>
                                                                                    </a>
                                                                                ) }
                                                                            </td>
                                                                        </tr>
                                                                        );
                                                                    }) }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </TabPanel>
                                                </TabView>
                                            </div>
                                        </div>
                                    );
                                }) }
                            </div>
                        ) }
                    </Col>

                    {/* Right: group info + clickable load numbers + group history */ }
                    <Col md={ 4 } lg={ 4 }>
                        <div style={ { position: 'sticky', top: 12 } }>
                            <div className="d-flex flex-column gap-3">
                                <div className="border rounded-3 p-4 bg-white shadow-sm">
                                    <div className="mb-2">
                                        <KeyValueRow label={ t("group token") } value={ groupToken } />
                                    </div>

                                    <div className="mb-2">
                                        <KeyValueRow
                                            label={ t("status") }
                                            value={ <AlpOutboundLoadGroupStatusChips row={ entity } t={ t } alwaysShowCounts={ true } /> }
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <KeyValueRow label={ t("qty") } value={ entity.qty ?? loads.length ?? 0 } />
                                    </div>

                                    <div className="mb-2">
                                        <KeyValueRow label={ t("author") }
                                                     value={ entity?.author?.name || entity?.author?.first_name || "-" } />
                                    </div>

                                    <div className="mb-2">
                                        <KeyValueRow label={ t("created at") }
                                                     value={ entity.created_at ? utcDateToLocalDate(entity.created_at) : "-" } />
                                    </div>

                                    <div className="mb-2">
                                        <KeyValueRow label={ t("updated at") }
                                                     value={ entity.updated_at ? utcDateToLocalDate(entity.updated_at) : "-" } />
                                    </div>

                                    <hr className="my-2" />

                                    <div>
                                        <KeyValueRow
                                            label={ t("load numbers") }
                                            value={
                                                <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                                    { groupLoadNumbers.length <= 0 && <span>-</span> }
                                                    { groupLoadNumbers.map((num: string) => (
                                                        <a
                                                            key={ num }
                                                            className="text-uppercase small cursor-pointer"
                                                            onClick={ () => {
                                                                scrollToLoadByNumber(num);
                                                            } }
                                                        >
                                                            { num }
                                                        </a>
                                                    )) }
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="border rounded-3 p-4 bg-white shadow-sm">
                                    <div className="text-muted small">{ t("status histories") }</div>
                                    <div
                                        ref={ historyContainerRef }
                                        style={ { maxHeight: 320, overflow: 'auto' } }
                                        className="mt-2"
                                    >
                                        <table className={ "fl-table w-full" }>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>{ t('author') }</th>
                                                    <th>{ t('status') }</th>
                                                    <th>{ t('comments') }</th>
                                                    <th>{ t('created at') }</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                { orderBy(histories, ['created_at'], ['desc']).map((h: any, idx: number) => {
                                                    const key = `${ h?.token ?? h?.id ?? idx }`;
                                                    const isHighlighted = highlightHistoryKey === key;
                                                    return (
                                                        <tr
                                                            key={ key }
                                                            ref={ (el) => {
                                                                historyRefs.current[key] = el;
                                                            } }
                                                            className={ isHighlighted ? 'table-primary' : '' }
                                                            onClick={ () => {
                                                                const el = historyRefs.current[key];
                                                                if ( el && historyContainerRef.current ) {
                                                                    const parent = historyContainerRef.current;
                                                                    parent.scrollTo({
                                                                        top: el.offsetTop - 40,
                                                                        behavior: 'smooth',
                                                                    });
                                                                }
                                                                highlightOnce('history', key);
                                                            } }
                                                            style={ { cursor: 'pointer' } }
                                                        >
                                                            <td>{ idx + 1 }</td>
                                                            <td>{ h?.author?.name ?? '-' }</td>
                                                            <td>
                                                                <AlpOutboundLoadStatus status={ h?.status } chip={ true } />
                                                            </td>
                                                            <td>
                                                                { h?.comments }
                                                                <div
                                                                    className="text-muted small">{ h?.system_note }</div>
                                                            </td>
                                                            <td>{ h?.created_at ? utcDateToLocalDate(h.created_at) : '-' }</td>
                                                        </tr>
                                                    );
                                                }) }
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            ) }
        </div>
    );
};

export default withTranslation()(AlpAvisoOutboundGroupDetailPage);

